import moment, { Moment } from 'moment';
import React, { useMemo, useRef, useState } from 'react';
import { Justify } from 'tea-component';
import { useDispatch, history } from 'umi';
import FilterCommon, { useFilterCommon } from '@opm/components/FilterCommon';
import MarketContentBody from '@opm/components/MarketContentBody';
import { dealwithBilibiliHashtag } from '@opm/components/MarketConnect/utils/videoAndHashtag';
import marketConnect from '@opm/components/MarketConnect';
import CardCommon from '@op/components/CardCommon';
import useIntls from '@opm/hooks/useIntls';
import { useStateRef } from '@op/hooks/common';
import { useBilibiliHashtagData } from '../../hooks/useBilibiliHashtagData';
import BilibiliHashtagTable from './BilibiliHashtagTable';
import TimeRangeSelect from './TimeRangeSelect';
import { ALL_HOURS, QUICK_OPTIONS, IQuickOption, getUtc8TodayRange } from './constants';
import type { IHashtagTabProps } from '../types';
import classNames from 'classnames';
import SvgIcon from '@home/components/SvgIcon';
import { UTCDateFilterTips } from '@op/components/BubbleCommon';
import './index.scss';

const BILIBILI_PRE_REQUEST_ID = 'hotspot/hashtag/bilibili/list';
const RangeDayNumber = 7;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function BilibiliTab({ defaultFilter, backCondConfig, hasData, hasDataDateRange }: IHashtagTabProps & Record<string, any>) {
  const { intl } = useIntls();
  const dispatch = useDispatch();

  const { filter, setFilter, loading, ...filterProps } = useFilterCommon({
    keys: ["MultiDateRange"],
  });
  // 标记 filter 变更来源，区分用户手动切换 vs 快捷选项内部触发
  const isInternalFilterUpdate = useRef(hasData);



  // 存储 shortid 恢复的时段值，在 confirm 回调中应用
  const shortIdTimeRange = useRef<{ startHour?: string; endHour?: string; activeQuickKey?: string } | undefined>(undefined);

  /** shortid 加载回调：保存恢复的时段到 ref */
  const handleShortIdLoaded = (extraData: Record<string, any>) => {
    if (extraData.startHour && extraData.endHour) {
      shortIdTimeRange.current = {
        startHour: extraData.startHour,
        endHour: extraData.endHour,
        activeQuickKey: extraData.activeQuickKey,
      };
    }
  };

  /** 创建 shortid 时注入当前时段值 */
  const handleShortIdExtraData = () => ({ startHour, endHour, activeQuickKey });

  // 时段状态：由 dealwithBilibiliHashtag 提供，无需 fallback
  // useStateRef 自动同步 ref，供 confirm 闭包读取最新值
  const [activeQuickKeyRef, activeQuickKey, setActiveQuickKey] = useStateRef<string>(hasData ? 'custom' : 'all');


  const [hours, setHours] = useState<string[]>(hasDataDateRange.hours);
  const [startHour, setStartHour] = useState(hasDataDateRange.startHour);
  const [endHour, setEndHour] = useState(hasDataDateRange.endHour);

  const { records, total, loading: dataLoading, download } = useBilibiliHashtagData({
    filter,
    hours,
  });

  /** UTC+8：禁用今天之后的日期 */
  const disabledDate = useMemo(
    () => ({
      date: (date: Moment) => {
        const todayUtc8 = moment().utcOffset(8).startOf('day');
        return !date.clone().utcOffset(8).startOf('day').isAfter(todayUtc8, 'day');
      },
    }),
    [],
  );

  // 快捷选项点击
  const handleQuickOption = (option: IQuickOption) => {
    setActiveQuickKey(option.key);
    const newHours = option.getHours();
    setHours(newHours);
    // 同步 TimeRangeSelect 展示
    if (option.key === 'all') {
      setStartHour('00:00');
      setEndHour('23:00');
      // 触发 shortid 更新（同步最新时段值）
      isInternalFilterUpdate.current = true;
      dispatch({
        type: 'opinionMarketingFilter/UPDATE_FILTER',
        payload: filterProps.filterRef?.current || {},
        instanceId: filterProps.instanceId,
      });
    } else {
      // 近N小时：dispatch 到 Redux 将 FilterCommon 日期设为今天
      const todayRange = getUtc8TodayRange();
      isInternalFilterUpdate.current = true;
      dispatch({
        type: 'opinionMarketingFilter/UPDATE_FILTER',
        payload: todayRange,
        instanceId: filterProps.instanceId,
      });
      // 同步 startHour/endHour
      const hourNums = newHours.map(h => parseInt(h, 10)).sort((a, b) => a - b);
      if (hourNums.length > 0 && hourNums[hourNums.length - 1] - hourNums[0] === hourNums.length - 1) {
        setStartHour(moment({ hour: hourNums[0] }).format('HH:mm'));
        setEndHour(moment({ hour: hourNums[hourNums.length - 1] }).format('HH:mm'));
      }
    }
  };

  // 自定义时段选择
  const handleTimeRangeChange = (start: string, end: string) => {
    setActiveQuickKey('custom');
    setStartHour(start);
    setEndHour(end);
    // 将时间范围转为 hours 数组
    const startH = parseInt(start, 10);
    const endH = parseInt(end, 10);
    const customHours: string[] = [];
    for (let i = startH; i <= endH; i++) {
      customHours.push(String(i).padStart(2, '0'));
    }
    setHours(customHours);
    // 触发 shortid 更新（同步最新时段值）
    isInternalFilterUpdate.current = true;
    dispatch({
      type: 'opinionMarketingFilter/UPDATE_FILTER',
      payload: filterProps.filterRef?.current || {},
      instanceId: filterProps.instanceId,
    });
  };
  return (
    <MarketContentBody
      loading={loading}
      filterSlot={
        <FilterCommon
          {...filterProps}
          userDefaultValue={defaultFilter}
          realTimeMode
          filterConfigProps={{
            "MultiDateRange": {
              tips: UTCDateFilterTips(8),
              showIcon: false,
              placement: "top",
              showShortcut: false,
              enableMaximumDuration: true,
              maximumDuration: {
                defaultNum: RangeDayNumber,
                defaultUnit: 'd' as const,
                tipsKey: 'Please select a time range within {N} days',
                tipsValues: { N: RangeDayNumber },
              },
              disabledDate,
            }
          }}
          shortIdExtraData={handleShortIdExtraData}
          onShortIdLoaded={handleShortIdLoaded}
          confirm={(value: any) => {
            setFilter(value);
            // 快捷选项内部触发的 filter 更新，不重置时段状态
            if (isInternalFilterUpdate.current) {
              isInternalFilterUpdate.current = false;
              return;
            }
            // shortid 恢复的时段值优先
            if (shortIdTimeRange.current) {
              const { startHour: sh, endHour: eh, activeQuickKey: qk } = shortIdTimeRange.current;
              shortIdTimeRange.current = undefined;
              setActiveQuickKey(qk || 'custom');
              setStartHour(sh);
              setEndHour(eh);
              const startH = parseInt(sh, 10);
              const endH = parseInt(eh, 10);
              const customHours: string[] = [];
              for (let i = startH; i <= endH; i++) {
                customHours.push(String(i).padStart(2, '0'));
              }
              setHours(customHours);
              return;
            }
            // 用户手动切换日期筛选时，自定义时段保留，快捷选项重置为 All Time
            if (activeQuickKeyRef.current === 'custom') {
              // 仅触发 shortid 更新，保持自定义时段不变
              isInternalFilterUpdate.current = true;
              dispatch({
                type: 'opinionMarketingFilter/UPDATE_FILTER',
                payload: filterProps.filterRef?.current || {},
                instanceId: filterProps.instanceId,
              });
            } else {
              setActiveQuickKey('all');
              setHours(ALL_HOURS);
              setStartHour('00:00');
              setEndHour('23:00');
            }
          }}
          backCondConfig={backCondConfig}
        />
      }
    >
      <CardCommon className="dbom-bili-hashtag-card"  >
        <Justify
          className='dbom-bili-hashtag-header'
          left={
            <div className="dbom-bili-hashtag-filter">
              <div className="dbom-bili-hashtag-filter-title">{intl("Choose Time")}</div>
              <div className="dbom-bili-hashtag-quick-options">
                {QUICK_OPTIONS.map((option) => (
                  <span
                    key={option.key}
                    className={classNames("dbom-bili-hashtag-quick-btn", { 'is-active': activeQuickKey === option.key })}
                    onClick={() => handleQuickOption(option)}
                  >
                    {option.label ? intl(option.label) : intl("Nearly {n} Hours", {
                      n: option.key
                    })}
                  </span>
                ))}
              </div>
              <TimeRangeSelect
                startHour={startHour}
                endHour={endHour}
                onChange={handleTimeRangeChange}
                isCustom={activeQuickKey === 'custom'}
              />
            </div>
          }
          right={
            <SvgIcon
              onClick={download}
              type="download"
              className="card-slot-charicon"
            />
          }
        />
        <CardCommon contentCard loading={dataLoading}
          loadingHeight={550}
          empty={records.length === 0}
          emptyHeight={550}>
          <BilibiliHashtagTable
            records={records}
            loading={false}
            filter={filter}
            hours={hours}
            total={total}
            isAllTime={activeQuickKey === 'all'}
          />
        </CardCommon>


      </CardCommon>
    </MarketContentBody>
  );
}

export default marketConnect<IHashtagTabProps & Record<string, any>>({
  ids: [BILIBILI_PRE_REQUEST_ID],
  dealwith: dealwithBilibiliHashtag(BILIBILI_PRE_REQUEST_ID),
  shouldSkipRequest: () => {
    const { query } = history.location || {};
    return Boolean(query?.shortid);
  },
})(BilibiliTab);
